import React, { useState, useEffect } from "react";
import {
  makeStyles, Paper, Grid, FormControl, InputLabel, MenuItem, TextField,
  Table, TableHead, TableBody, TableCell, TableRow, IconButton, Select,
  Button, Drawer, Typography, InputAdornment, Tooltip
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import {
  Edit as EditIcon, Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
  Close as CloseIcon, Add as AddIcon, Search as SearchIcon,
  Business as BusinessIcon, CheckCircleOutline as CheckIcon,
  Schedule as ScheduleIcon, ErrorOutline as WarningIcon,
  EventOutlined as EventIcon, InfoOutlined as InfoIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import ModalUsers from "../ModalUsers";
import api from "../../services/api";
import { head, isArray } from "lodash";
import { useDate } from "../../hooks/useDate";
import moment from "moment";
import { i18n } from "../../translate/i18n";

const BLUE = "#1745ed";
const useStyles = makeStyles((theme) => ({
  root: { width: "100%", fontFamily: "inherit", color: theme.palette.text.primary },
  mainPaper: { background: "transparent", boxShadow: "none", width: "100%" },
  surface: {
    background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`,
    borderRadius: 15, boxShadow: theme.palette.type === "dark" ? "none" : "0 7px 28px rgba(21,42,89,.035)"
  },
  toolbar: { padding: "19px 20px", marginBottom: 14 },
  toolbarRow: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 },
  search: { flex: "1 1 280px", minWidth: 190, "& .MuiOutlinedInput-root": { borderRadius: 9, background: theme.palette.background.paper } },
  filter: { minWidth: 150, flex: "0 1 174px", "& .MuiOutlinedInput-root": { borderRadius: 9 } },
  primaryButton: { background: BLUE, color: "white", borderRadius: 9, minHeight: 42, textTransform: "none", fontWeight: 700, padding: "0 17px", whiteSpace: "nowrap", boxShadow: "0 4px 10px rgba(23,69,237,.15)", "&:hover": { background: "#1038c7" } },
  summaries: { display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 12, marginTop: 18, "@media (max-width: 880px)": { gridTemplateColumns: "repeat(2,minmax(0,1fr))" }, "@media (max-width: 450px)": { gridTemplateColumns: "1fr" } },
  summary: { minWidth: 0, display: "flex", gap: 12, alignItems: "center", borderRadius: 11, padding: 15, border: `1px solid ${theme.palette.divider}`, background: theme.palette.type === "dark" ? theme.palette.background.default : "#fbfcff" },
  summaryIcon: { width: 42, height: 42, borderRadius: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  metric: { fontWeight: 750, fontSize: 24, lineHeight: 1.2 },
  summaryLabel: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
  tableShell: { overflowX: "auto", borderRadius: 14 },
  table: { minWidth: 975, "& .MuiTableCell-root": { borderBottom: `1px solid ${theme.palette.divider}`, padding: "12px 10px", fontSize: 12 }, "& .MuiTableCell-head": { fontWeight: 700, color: theme.palette.text.secondary, background: theme.palette.type === "dark" ? theme.palette.background.paper : "#f7f9fc", whiteSpace: "nowrap" } },
  companyName: { display: "flex", alignItems: "center", gap: 9, minWidth: 110, fontWeight: 650 },
  companyIcon: { background: theme.palette.type === "dark" ? theme.palette.background.default : "#eaf1ff", color: BLUE, borderRadius: 9, width: 30, height: 30, display: "flex", justifyContent: "center", alignItems: "center", flexShrink: 0 },
  pill: { display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 20, padding: "5px 10px", fontSize: 11, fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap" },
  dateWarning: { color: "#c36b08", fontWeight: 700 }, dateDanger: { color: "#d92a41", fontWeight: 700 },
  rowHover: { "&:hover": { background: theme.palette.action.hover } },
  actionIcon: { border: `1px solid ${theme.palette.divider}`, borderRadius: 8, padding: 6, marginLeft: 4 },
  bottomBar: { display: "flex", justifyContent: "space-between", flexWrap: "wrap", alignItems: "center", gap: 10, padding: "13px 17px", color: theme.palette.text.secondary, fontSize: 12 },
  legend: { display: "flex", gap: 17, flexWrap: "wrap", fontSize: 11, alignItems: "center", marginTop: 12, color: theme.palette.text.secondary },
  legendDot: { display: "inline-block", width: 9, height: 9, borderRadius: "50%", marginRight: 6 },
  drawerPaper: { width: 472, maxWidth: "100vw", boxSizing: "border-box", background: theme.palette.background.paper, "@media (max-width: 600px)": { width: "100vw" } },
  drawerHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, padding: "22px 24px 18px", borderBottom: `1px solid ${theme.palette.divider}` },
  drawerTitle: { fontWeight: 750, fontSize: 20, margin: 0 },
  drawerDescription: { marginTop: 4, color: theme.palette.text.secondary, fontSize: 12 },
  drawerContent: { flex: 1, overflowY: "auto", padding: 18, "&::-webkit-scrollbar": { width: 7 }, "&::-webkit-scrollbar-thumb": { background: "#cfd5e1", borderRadius: 12 } },
  section: { border: `1px solid ${theme.palette.divider}`, borderRadius: 12, padding: 15, marginBottom: 13 },
  sectionHead: { display: "flex", alignItems: "center", gap: 9, marginBottom: 14, fontWeight: 700, fontSize: 13 },
  sectionIcon: { color: BLUE, background: theme.palette.type === "dark" ? theme.palette.background.default : "#edf3ff", borderRadius: 9, padding: 7, display: "flex" },
  field: { width: "100%", "& .MuiOutlinedInput-root": { borderRadius: 8 }, "& .MuiInputBase-input": { fontSize: 13 } },
  drawerFooter: { padding: "15px 19px", borderTop: `1px solid ${theme.palette.divider}`, display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 9, background: theme.palette.background.paper },
  detailHero: { display: "flex", alignItems: "center", gap: 13, marginBottom: 17 },
  detailIcon: { width: 54, height: 54, display: "flex", alignItems: "center", justifyContent: "center", color: BLUE, background: theme.palette.type === "dark" ? theme.palette.background.default : "#eaf1ff", borderRadius: 14 },
  alert: { padding: 13, display: "flex", alignItems: "flex-start", gap: 9, borderRadius: 10, marginBottom: 15, fontSize: 12 },
  detailLine: { display: "flex", gap: 12, justifyContent: "space-between", padding: "11px 0", borderBottom: `1px solid ${theme.palette.divider}`, fontSize: 12 },
  detailLabel: { color: theme.palette.text.secondary }, detailValue: { maxWidth: "60%", textAlign: "right", overflowWrap: "anywhere", fontWeight: 600 },
}));

const EMPTY_RECORD = {
  name: "", email: "", phone: "", planId: "", status: true,
  dueDate: "", recurrence: "", password: "", document: "", paymentMethod: ""
};

// Indicadores puramente visuais. Não alteram a situação da empresa nem o controle de acesso.
const dueInfo = (company) => {
  if (!company || !company.dueDate || !moment(company.dueDate).isValid()) return null;
  const days = moment(company.dueDate).startOf("day").diff(moment().startOf("day"), "days");
  if (days < 0) return { type: "expired", label: "Vencida", days: Math.abs(days), color: "#c92842", bg: "#ffedf0" };
  if (days === 0) return { type: "soon", label: "Vence hoje", days: 0, color: "#b66909", bg: "#fff4e2" };
  if (days <= 5) return { type: "soon", label: "Vence em breve", days, color: "#b66909", bg: "#fff4e2" };
  return { type: "valid", label: "Ativa", days, color: "#138258", bg: "#e7f7ef" };
};

const companyVisualStatus = (company) => {
  if (company.status === false) return { label: "Inativa", color: "#64748b", bg: "#edf0f5", type: "inactive" };
  return dueInfo(company) || { label: "Ativa", color: "#138258", bg: "#e7f7ef", type: "valid" };
};

const companyPlan = (company) => (company.plan && company.plan.name) || "-";
const companyAmount = (company) => {
  const raw = company.plan && company.plan.amount;
  return raw !== null && raw !== undefined && raw !== ""
    ? `R$ ${Number(raw).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-";
};

const StatusPill = ({ info, classes }) => (
  <span className={classes.pill} style={{ color: info.color, background: info.bg }}>
    <span style={{ width: 7, height: 7, background: info.color, borderRadius: "50%" }} />{info.label}
  </span>
);

export function CompanyForm({ onSubmit, onDelete, onCancel, initialValue, loading }) {
  const classes = useStyles();
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [record, setRecord] = useState({ ...EMPTY_RECORD, ...initialValue });
  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchPlans() {
      try { setPlans(await listPlans()); } catch (e) { toast.error("Não foi possível carregar os planos"); }
    }
    fetchPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const source = { ...EMPTY_RECORD, ...initialValue };
    if (source.dueDate && moment(source.dueDate).isValid()) {
      source.dueDate = moment(source.dueDate).format("YYYY-MM-DD");
    }
    setRecord(source);
    setShowPassword(false);
  }, [initialValue]);

  const handleSubmit = (values) => {
    const data = { ...values };
    if (data.dueDate === "" || moment(data.dueDate).isValid() === false) data.dueDate = null;
    onSubmit(data);
  };

  // Funcionalidade preexistente, mantida para compatibilidade.
  // eslint-disable-next-line no-unused-vars
  const handleOpenModalUsers = async () => {
    try {
      const { data } = await api.get("/users/list", { params: { companyId: initialValue.id } });
      if (isArray(data) && data.length) setFirstUser(head(data));
      setModalUser(true);
    } catch (e) { toast.error(e); }
  };
  const handleCloseModalUsers = () => { setFirstUser({}); setModalUser(false); };
  const incrementDueDate = () => {
    const data = { ...record };
    if (data.dueDate !== "" && data.dueDate !== null) {
      switch (data.recurrence) {
        case "MENSAL": data.dueDate = moment(data.dueDate).add(1, "month").format("YYYY-MM-DD"); break;
        case "BIMESTRAL": data.dueDate = moment(data.dueDate).add(2, "month").format("YYYY-MM-DD"); break;
        case "TRIMESTRAL": data.dueDate = moment(data.dueDate).add(3, "month").format("YYYY-MM-DD"); break;
        case "SEMESTRAL": data.dueDate = moment(data.dueDate).add(6, "month").format("YYYY-MM-DD"); break;
        case "ANUAL": data.dueDate = moment(data.dueDate).add(12, "month").format("YYYY-MM-DD"); break;
        default: break;
      }
    }
    setRecord(data);
  };

  return (
    <>
      <ModalUsers userId={firstUser.id} companyId={initialValue.id} open={modalUser} onClose={handleCloseModalUsers} />
      <Formik enableReinitialize initialValues={record} onSubmit={handleSubmit}>
        {() => (
          <Form id="company-settings-form" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <div className={classes.drawerContent}>
              <div className={classes.section}>
                <div className={classes.sectionHead}><span className={classes.sectionIcon}><BusinessIcon fontSize="small" /></span> Dados da empresa</div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}><Field as={TextField} label={i18n.t("compaies.table.name")} name="name" variant="outlined" margin="dense" className={classes.field} /></Grid>
                  <Grid item xs={12} sm={6}><Field as={TextField} label={i18n.t("compaies.table.email")} name="email" variant="outlined" margin="dense" required className={classes.field} /></Grid>
                  <Grid item xs={12} sm={6}><Field as={TextField} label={i18n.t("compaies.table.phone")} name="phone" variant="outlined" margin="dense" className={classes.field} /></Grid>
                  <Grid item xs={12} sm={6}><Field as={TextField} label={i18n.t("compaies.table.document")} name="document" variant="outlined" margin="dense" className={classes.field} /></Grid>
                </Grid>
              </div>
              <div className={classes.section}>
                <div className={classes.sectionHead}><span className={classes.sectionIcon}><EventIcon fontSize="small" /></span> Plano e assinatura</div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl margin="dense" variant="outlined" fullWidth>
                      <InputLabel htmlFor="plan-selection">{i18n.t("compaies.table.plan")}</InputLabel>
                      <Field as={Select} id="plan-selection" label={i18n.t("compaies.table.plan")} labelId="plan-selection-label" name="planId" required>
                        {plans.map(plan => <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>)}
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl margin="dense" variant="outlined" fullWidth>
                      <InputLabel htmlFor="recurrence-selection">{i18n.t("compaies.table.recurrence")}</InputLabel>
                      <Field as={Select} id="recurrence-selection" label={i18n.t("compaies.table.recurrence")} name="recurrence">
                        <MenuItem value="MENSAL">{i18n.t("compaies.table.monthly")}</MenuItem>
                        <MenuItem value="BIMESTRAL">{i18n.t("compaies.table.bimonthly")}</MenuItem>
                        <MenuItem value="TRIMESTRAL">{i18n.t("compaies.table.quarterly")}</MenuItem>
                        <MenuItem value="SEMESTRAL">{i18n.t("compaies.table.semester")}</MenuItem>
                        <MenuItem value="ANUAL">{i18n.t("compaies.table.yearly")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Field as={TextField} label={i18n.t("compaies.table.dueDate")} type="date" name="dueDate"
                      InputLabelProps={{ shrink: true }} variant="outlined" fullWidth margin="dense" className={classes.field} />
                  </Grid>
                </Grid>
              </div>
              <div className={classes.section}>
                <div className={classes.sectionHead}><span className={classes.sectionIcon}><InfoIcon fontSize="small" /></span> Acesso e situação</div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={7}>
                    <Field as={TextField} label={i18n.t("compaies.table.password")} name="password"
                      type={showPassword ? "text" : "password"} variant="outlined" className={classes.field} margin="dense"
                      InputProps={{ endAdornment: <InputAdornment position="end"><IconButton size="small" aria-label={showPassword ? "Ocultar senha" : "Mostrar senha digitada"} onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}</IconButton></InputAdornment> }} />
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <FormControl margin="dense" variant="outlined" fullWidth>
                      <InputLabel htmlFor="company-status-selection">{i18n.t("compaies.table.active")}</InputLabel>
                      <Field as={Select} id="company-status-selection" label={i18n.t("compaies.table.active")} name="status">
                        <MenuItem value={true}>{i18n.t("compaies.table.yes")}</MenuItem>
                        <MenuItem value={false}>{i18n.t("compaies.table.no")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                </Grid>
                {record.id !== undefined && <Typography style={{ fontSize: 11, color: "#7b8598", marginTop: 6 }}>A senha atual não é exibida; o olho mostra somente a senha digitada neste formulário.</Typography>}
              </div>
            </div>
            <div className={classes.drawerFooter}>
              <Button onClick={onCancel} variant="outlined" disabled={loading} style={{ borderRadius: 8, textTransform: "none" }}>{i18n.t("compaies.table.clear")}</Button>
              {record.id !== undefined && <>
                <ButtonWithSpinner loading={loading} onClick={() => onDelete(record)} variant="outlined" color="secondary" style={{ textTransform: "none", borderRadius: 8 }}>{i18n.t("compaies.table.delete")}</ButtonWithSpinner>
                <ButtonWithSpinner loading={loading} onClick={incrementDueDate} variant="outlined" color="primary" style={{ textTransform: "none", borderRadius: 8 }}>{i18n.t("compaies.table.dueDate")}</ButtonWithSpinner>
              </>}
              <ButtonWithSpinner loading={loading} type="submit" variant="contained" color="primary" style={{ background: BLUE, color: "white", textTransform: "none", fontWeight: 700, borderRadius: 8 }}>{i18n.t("compaies.table.save")}</ButtonWithSpinner>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}

export function CompaniesManagerGrid({ records, onSelect, onView }) {
  const classes = useStyles();
  const { dateToClient, datetimeToClient } = useDate();
  return <Paper className={`${classes.surface} ${classes.tableShell}`} elevation={0}>
    <Table className={classes.table} size="small" aria-label="Lista de empresas">
      <TableHead><TableRow>
        <TableCell>#</TableCell><TableCell>Empresa</TableCell><TableCell>E-mail</TableCell><TableCell>Telefone</TableCell>
        <TableCell>Plano</TableCell><TableCell>Valor</TableCell><TableCell>Situação</TableCell>
        <TableCell>Vencimento</TableCell><TableCell>Criada em</TableCell><TableCell>Último login</TableCell><TableCell align="right">Ações</TableCell>
      </TableRow></TableHead>
      <TableBody>
        {records.map((row) => {
          const status = companyVisualStatus(row);
          const expiration = dueInfo(row);
          return <TableRow key={row.id} className={classes.rowHover}>
            <TableCell>{row.id}</TableCell>
            <TableCell><div className={classes.companyName}><span className={classes.companyIcon}><BusinessIcon fontSize="small" /></span><span>{row.name || "-"}</span></div></TableCell>
            <TableCell>{row.email || "-"}</TableCell>
            <TableCell>{row.phone || "-"}</TableCell>
            <TableCell>{companyPlan(row)}</TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>{companyAmount(row)}</TableCell>
            <TableCell><StatusPill info={status} classes={classes}/></TableCell>
            <TableCell><span className={expiration && expiration.type === "expired" ? classes.dateDanger : expiration && expiration.type === "soon" ? classes.dateWarning : ""}>
              {row.dueDate ? dateToClient(row.dueDate) : "-"}
            </span>{expiration && expiration.type !== "valid" && <div style={{ fontSize: 10, fontWeight: 600, whiteSpace: "nowrap", marginTop: 3 }}>
              {expiration.days === 0 ? "Vence hoje" : expiration.type === "expired" ? `${expiration.days} dia(s) em atraso` : `Faltam ${expiration.days} dia(s)`}
            </div>}</TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>{row.createdAt ? dateToClient(row.createdAt) : "-"}</TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>{row.lastLogin ? datetimeToClient(row.lastLogin) : "-"}</TableCell>
            <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
              <Tooltip title="Visualizar empresa"><IconButton className={classes.actionIcon} size="small" aria-label={`Visualizar ${row.name}`} onClick={() => onView(row)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Editar empresa"><IconButton className={classes.actionIcon} size="small" aria-label={`Editar ${row.name}`} onClick={() => onSelect(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
            </TableCell>
          </TableRow>;
        })}
        {!records.length && <TableRow><TableCell colSpan={11} align="center" style={{ padding: "44px 15px" }}>Nenhuma empresa encontrada para os filtros selecionados.</TableCell></TableRow>}
      </TableBody>
    </Table>
  </Paper>;
}

function CompanyDetails({ record, onClose, onEdit }) {
  const classes = useStyles();
  const { dateToClient, datetimeToClient } = useDate();
  const status = companyVisualStatus(record);
  const exp = dueInfo(record);
  const [counts, setCounts] = useState({ users: null, connections: null });
  useEffect(() => {
    let mounted = true;
    setCounts({ users: null, connections: null });
    // Consultas apenas de leitura; não alteram usuários, conexões ou permissões.
    api.get("/users/list", { params: { companyId: record.id } })
      .then(({ data }) => { if (mounted && Array.isArray(data)) setCounts(prev => ({ ...prev, users: data.length })); })
      .catch(() => {});
    api.get("/whatsapp/all", { params: { session: 0 } })
      .then(({ data }) => {
        if (mounted && Array.isArray(data)) {
          setCounts(prev => ({ ...prev, connections: data.filter(w => String(w.companyId) === String(record.id)).length }));
        }
      }).catch(() => {});
    return () => { mounted = false; };
  }, [record.id]);
  const lines = [
    ["Nome da empresa", record.name], ["E-mail", record.email], ["Telefone", record.phone],
    ["CNPJ/CPF", record.document], ["Plano", companyPlan(record)], ["Valor", companyAmount(record)],
    ["Recorrência", record.recurrence], ["Situação cadastral", record.status === false ? "Inativa" : "Ativa"],
    ["Vencimento", record.dueDate ? dateToClient(record.dueDate) : "Não informado"],
    ["Criada em", record.createdAt ? dateToClient(record.createdAt) : "-"],
    ["Último login", record.lastLogin ? datetimeToClient(record.lastLogin) : "-"],
    ["Usuários", counts.users === null ? "Não disponível" : `${counts.users} usuário(s)`],
    ["Conexões", counts.connections === null ? "Não disponível" : `${counts.connections} conexão(ões)`],
  ];
  return <>
    <div className={classes.drawerHeader}>
      <div><h2 className={classes.drawerTitle}>Visualizar empresa</h2><div className={classes.drawerDescription}>Informações da empresa (somente leitura)</div></div>
      <IconButton aria-label="Fechar detalhes da empresa" onClick={onClose} size="small"><CloseIcon /></IconButton>
    </div>
    <div className={classes.drawerContent}>
      <div className={classes.detailHero}>
        <span className={classes.detailIcon}><BusinessIcon style={{ fontSize: 30 }}/></span>
        <div><Typography style={{ fontSize: 18, fontWeight: 750 }}>{record.name || "Empresa"}</Typography><Typography style={{ fontSize: 12, color: "#7b8598" }}>{record.document || `Empresa #${record.id}`}</Typography></div>
      </div>
      {exp && (exp.type === "soon" || exp.type === "expired") && <div className={classes.alert} style={{ background: exp.bg, color: exp.color }}>
        <WarningIcon fontSize="small" />
        <div><strong>{exp.days === 0 ? "A assinatura vence hoje" : exp.type === "expired" ? `Assinatura vencida há ${exp.days} dia(s)` : `A assinatura vence em ${exp.days} dia(s)`}</strong>
          <div style={{ marginTop: 4, opacity: .82 }}>Aviso visual baseado no vencimento cadastrado. Nenhum bloqueio é aplicado por esta tela.</div>
        </div>
      </div>}
      <div className={classes.section}>
        <div className={classes.sectionHead}><span className={classes.sectionIcon}><InfoIcon fontSize="small" /></span> Informações e assinatura</div>
        {lines.map(([name, value]) => <div className={classes.detailLine} key={name}>
          <span className={classes.detailLabel}>{name}</span>
          <span className={classes.detailValue}>{value || "-"}</span>
        </div>)}
        <div className={classes.detailLine}><span className={classes.detailLabel}>Status de vencimento</span><StatusPill info={status} classes={classes}/></div>
      </div>
    </div>
    <div className={classes.drawerFooter}>
      <Button variant="outlined" onClick={onClose} style={{ textTransform: "none" }}>Fechar</Button>
      <Button className={classes.primaryButton} onClick={() => onEdit(record)} startIcon={<EditIcon/>}>Editar empresa</Button>
    </div>
  </>;
}

export default function CompaniesManager() {
  const classes = useStyles();
  const { list, save, update, remove } = useCompanies();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({ ...EMPTY_RECORD });
  const [drawer, setDrawer] = useState(null);
  const [previewRecord, setPreviewRecord] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try { setRecords(await list()); }
    catch (e) { toast.error("Não foi possível carregar a lista de registros"); }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) await update(data);
      else await save(data);
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error("Não foi possível realizar a operação. Verifique se já existe uma empresa com o mesmo nome ou se os campos foram preenchidos corretamente");
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) { toast.error("Não foi possível realizar a operação"); }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => setShowConfirmDialog(true);
  const handleCancel = () => { setRecord({ ...EMPTY_RECORD }); setDrawer(null); setPreviewRecord(null); };
  const handleSelect = (data) => {
    setRecord({
      ...EMPTY_RECORD, id: data.id, name: data.name || "", phone: data.phone || "", email: data.email || "",
      planId: data.planId || (data.plan && data.plan.id) || "", status: data.status === false ? false : true,
      dueDate: data.dueDate || "", recurrence: data.recurrence || "", password: "",
      document: data.document || "", paymentMethod: data.paymentMethod || "",
    });
    setDrawer("form");
  };
  const handleNew = () => { setRecord({ ...EMPTY_RECORD }); setDrawer("form"); };
  const handleView = (data) => { setPreviewRecord(data); setDrawer("detail"); };

  const categories = {
    total: records.length,
    active: records.filter(item => item.status !== false && (!dueInfo(item) || dueInfo(item).type !== "expired")).length,
    soon: records.filter(item => item.status !== false && dueInfo(item) && dueInfo(item).type === "soon").length,
    expired: records.filter(item => dueInfo(item) && dueInfo(item).type === "expired").length
  };
  const plans = Array.from(new Map(records.filter(item => item.plan && item.plan.id !== undefined).map(item => [String(item.plan.id), item.plan.name])).entries());
  const normalized = search.trim().toLocaleLowerCase("pt-BR");
  const filtered = records.filter(item => {
    const matchesText = !normalized || [item.name, item.email, item.phone].some(value => String(value || "").toLocaleLowerCase("pt-BR").includes(normalized));
    const matchesPlan = planFilter === "all" || String(item.planId || (item.plan && item.plan.id)) === planFilter;
    const status = companyVisualStatus(item);
    const matchesStatus = statusFilter === "all" || status.type === statusFilter;
    return matchesText && matchesPlan && matchesStatus;
  });

  return <Paper className={classes.mainPaper} elevation={0}>
    <div className={`${classes.surface} ${classes.toolbar}`}>
      <div className={classes.toolbarRow}>
        <Button className={classes.primaryButton} onClick={handleNew} startIcon={<AddIcon />}>Nova empresa</Button>
        <TextField className={classes.search} value={search} onChange={(e) => setSearch(e.target.value)} variant="outlined" size="small"
          placeholder="Buscar empresa por nome, e-mail ou telefone..." InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: "#8994a9" }}/></InputAdornment> }} inputProps={{ "aria-label": "Buscar empresas" }}/>
        <TextField className={classes.filter} select label="Situação" variant="outlined" size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <MenuItem value="all">Todos os status</MenuItem>
          <MenuItem value="valid">Ativas</MenuItem>
          <MenuItem value="soon">Vencendo em breve</MenuItem>
          <MenuItem value="expired">Vencidas</MenuItem>
          <MenuItem value="inactive">Inativas</MenuItem>
        </TextField>
        <TextField className={classes.filter} select label="Plano" variant="outlined" size="small" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
          <MenuItem value="all">Todos os planos</MenuItem>
          {plans.map(([id, name]) => <MenuItem key={id} value={id}>{name}</MenuItem>)}
        </TextField>
      </div>
      <div className={classes.summaries}>
        {[
          { label: "Total de empresas", count: categories.total, icon: <BusinessIcon />, color: "#2563eb", background: "#e8f1ff" },
          { label: "Ativas", count: categories.active, icon: <CheckIcon />, color: "#139562", background: "#e6f8ef" },
          { label: "Vencendo em breve", count: categories.soon, icon: <ScheduleIcon />, color: "#cc7a09", background: "#fff3df" },
          { label: "Vencidas", count: categories.expired, icon: <WarningIcon />, color: "#dd314d", background: "#ffebee" },
        ].map(item => <div className={classes.summary} key={item.label}>
          <div className={classes.summaryIcon} style={{ background: item.background, color: item.color }}>{item.icon}</div>
          <div><div className={classes.metric} style={{ color: item.color }}>{item.count}</div><div className={classes.summaryLabel}>{item.label}</div></div>
        </div>)}
      </div>
    </div>
    <CompaniesManagerGrid records={filtered} onSelect={handleSelect} onView={handleView}/>
    <div className={classes.bottomBar}>
      <span>Mostrando {filtered.length} de {records.length} empresa(s)</span>
      <div className={classes.legend}>
        <span><span className={classes.legendDot} style={{ background: "#138258" }}/>Ativa</span>
        <span><span className={classes.legendDot} style={{ background: "#b66909" }}/>Vence em breve (até 5 dias)</span>
        <span><span className={classes.legendDot} style={{ background: "#c92842" }}/>Vencida</span>
        <span><span className={classes.legendDot} style={{ background: "#64748b" }}/>Inativa</span>
      </div>
    </div>
    <Drawer anchor="right" open={Boolean(drawer)} onClose={() => setDrawer(null)} classes={{ paper: classes.drawerPaper }}>
      {drawer === "form" && <>
        <div className={classes.drawerHeader}>
          <div><h2 className={classes.drawerTitle}>{record.id !== undefined ? "Editar empresa" : "Cadastrar empresa"}</h2><div className={classes.drawerDescription}>Preencha ou atualize os dados da empresa.</div></div>
          <IconButton aria-label="Fechar formulário" onClick={handleCancel} size="small"><CloseIcon /></IconButton>
        </div>
        <CompanyForm initialValue={record} onDelete={handleOpenDeleteDialog} onSubmit={handleSubmit} onCancel={handleCancel} loading={loading}/>
      </>}
      {drawer === "detail" && previewRecord && <CompanyDetails record={previewRecord} onClose={() => setDrawer(null)} onEdit={handleSelect}/>}
    </Drawer>
    <ConfirmationModal title="Exclusão de Registro" open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} onConfirm={() => handleDelete()}>
      Deseja realmente excluir esse registro?
    </ConfirmationModal>
  </Paper>;
}
